import { useState } from 'react';
import { useToast } from '@/components/Toast';
import { MultiClientPicker, type SelectedClient } from '@/components/MultiClientPicker';
import { ApiError } from '@/api/client';
import type { ClientFormBody } from '@/api/hooks';

const todayISO = () => new Date().toISOString().slice(0, 10);

interface SubmitLike {
  mutateAsync: (body: ClientFormBody) => Promise<{ submissionId: number }>;
  isPending: boolean;
}

/** Shared client request form (Termination / Reactivation): a multi-select client
 *  picker, an effective date, notes, and Submit. Differs only by client scope,
 *  labels, and which submit mutation is passed in. */
export function ClientRequestForm({
  title, description, scope, dateLabel, submit,
}: {
  title: string;
  description: string;
  scope: 'active' | 'all';
  dateLabel: string;
  submit: SubmitLike;
}) {
  const notify = useToast();
  const [clients, setClients] = useState<SelectedClient[]>([]);
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [notes, setNotes] = useState('');

  const invalid = clients.length === 0 || !effectiveDate;

  async function onSubmit() {
    try {
      const res = await submit.mutateAsync({
        clients: clients.map((c) => ({ clientId: c.clientId, clientName: c.clientName })),
        effectiveDate,
        notes,
      });
      notify('success', `${title.replace(/ Request$/, '')} request #${res.submissionId} submitted. A receipt has been emailed to you.`);
      setClients([]);
      setEffectiveDate(todayISO());
      setNotes('');
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Submit failed');
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-primary">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="label">Client(s) *</label>
          <MultiClientPicker scope={scope} value={clients} onChange={setClients} disabled={submit.isPending} />
        </div>

        <div className="max-w-xs">
          <label className="label">{dateLabel} *</label>
          <input type="date" className="input" value={effectiveDate} disabled={submit.isPending} onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>

        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={notes} disabled={submit.isPending} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

        <div>
          <button className="btn-primary" disabled={invalid || submit.isPending} onClick={onSubmit}>
            {submit.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
